import { useEffect, useRef } from "react";
import { AlertTriangle, ArrowLeft, Download, RotateCcw, ShieldCheck } from "lucide-react";
import type {
  CaptureState,
  MatterDraft,
  TranscriptRow,
  TranscriptSession
} from "../app/ui-types";
import { useLiveSession } from "../hooks/useLiveSession";
import { displayTranscriptTitle } from "../app/transcript-title";

type RecoveryWorkspaceProps = {
  matter: MatterDraft;
  matterId?: string;
  previousSessionCount: number;
  onBack: () => void;
  onSessionChange: (session: TranscriptSession) => void;
  onCaptureStateChange: (state: CaptureState) => void;
  onRecovered: (
    rows: TranscriptRow[],
    session: TranscriptSession,
    audioUrl: string | null
  ) => void;
};

const formatStartedAt = (value: number | null) => {
  if (!value) return "Unknown start time";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
};

export function RecoveryWorkspace({
  matter,
  matterId,
  previousSessionCount,
  onBack,
  onSessionChange,
  onCaptureStateChange,
  onRecovered
}: RecoveryWorkspaceProps) {
  const session = useLiveSession(matter, onSessionChange, onCaptureStateChange, {
    matterId,
    previousSessionCount
  });
  const recoveryOpened = useRef(false);
  const title = displayTranscriptTitle(matter.title, matter.date);
  const canRecover = session.recoveryAvailable && session.recoveryStatus === "local-evidence";
  const recovering =
    session.recoveryStatus === "recovering-local-evidence" ||
    session.recoveryStatus === "preparing-review" ||
    session.state === "processing";
  const unavailable =
    session.recoveryStatus === "unavailable" ||
    session.recoveryStatus === "error" ||
    session.recoveryStatus === "quarantined";

  useEffect(() => {
    if (
      session.state !== "review" ||
      recoveryOpened.current ||
      !session.transcriptSession
    ) {
      return;
    }
    recoveryOpened.current = true;
    const rows = session.rows.map((row) => ({
      ...row,
      recordedAtMs:
        row.recordedAtMs ??
        (session.recordingStartedAt
          ? session.recordingStartedAt + row.startMs
          : undefined)
    }));
    onRecovered(
      rows,
      {
        ...session.transcriptSession,
        recordedAtEpochMs:
          session.transcriptSession.recordedAtEpochMs ??
          session.recordingStartedAt ??
          undefined
      },
      session.sourceAudioUrl
    );
  }, [
    onRecovered,
    session.recordingStartedAt,
    session.rows,
    session.sourceAudioUrl,
    session.state,
    session.transcriptSession
  ]);

  return (
    <section className="recovery-workspace" aria-labelledby="recovery-workspace-title">
      <div className="recovery-workspace__bar">
        <button className="button button--text" type="button" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" />
          Transcripts
        </button>
      </div>

      <div className="recovery-panel">
        <header className="recovery-panel__head">
          <span className="recovery-panel__icon" aria-hidden="true">
            {unavailable ? <AlertTriangle size={22} /> : <ShieldCheck size={22} />}
          </span>
          <div>
            <p className="eyebrow">Recovery</p>
            <h1 id="recovery-workspace-title">{title}</h1>
            <p>
              Interrupted session from {formatStartedAt(session.recordingStartedAt)}.
              Recovery is only for a crash, offline interruption, or failed upload. The
              interruption will be marked in the transcript record.
            </p>
          </div>
        </header>

        <dl className="recovery-ledgers" aria-label="Preserved local evidence">
          <div>
            <dt>Source audio</dt>
            <dd>
              {session.recoveryEvidence.source
                ? `${session.bufferedChunks} chunk${session.bufferedChunks === 1 ? "" : "s"} preserved`
                : "No local source copy found"}
            </dd>
          </div>
          <div>
            <dt>Analysis copy</dt>
            <dd>
              {session.recoveryEvidence.analysis
                ? `${session.analysisBufferedChunks} chunk${
                    session.analysisBufferedChunks === 1 ? "" : "s"
                  } preserved`
                : "No local analysis copy found"}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              {recovering
                ? "Recovering evidence"
                : canRecover
                  ? "Ready to recover"
                  : unavailable
                    ? "Recovery unavailable on this device"
                    : "Checking this device"}
            </dd>
          </div>
        </dl>

        {session.error && (
          <p className="recovery-panel__notice" role="status">
            {session.error}
          </p>
        )}

        <div className="recovery-panel__actions">
          <button
            className="button button--primary"
            type="button"
            onClick={() => void session.recover()}
            disabled={!canRecover || recovering}
          >
            <RotateCcw size={17} aria-hidden="true" />
            Recover and prepare transcript
          </button>
          {unavailable && (
            <button
              className="button button--quiet"
              type="button"
              onClick={session.retryRecovery}
            >
              Check server again
            </button>
          )}
          <button className="button button--quiet" type="button" onClick={onBack}>
            Back to transcripts
          </button>
        </div>

        {(session.recoveryEvidence.source || session.recoveryEvidence.analysis) && (
          <div className="recovery-downloads" aria-label="Evidence downloads">
            <button
              className="button button--quiet"
              type="button"
              onClick={session.downloadSource}
              disabled={!session.recoveryEvidence.source}
            >
              <Download size={16} aria-hidden="true" />
              Download source audio
            </button>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void session.downloadAnalysis()}
              disabled={!session.recoveryEvidence.analysis}
            >
              <Download size={16} aria-hidden="true" />
              Download analysis copy
            </button>
            <button
              className="button button--text"
              type="button"
              onClick={session.resetRecovery}
              disabled={!session.canReleaseRecovery}
            >
              Download evidence and clear recovery
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
